import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';

import { webauthnBeginLogin, webauthnFinishLogin } from '@column/api';
import { Button } from '@column/column-ui-kit';

import { CompletionCheck } from '~/components';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { getPublicKeyCredential, isWebAuthnSupported } from '~/lib/webauthn';
import { ChallengeName, DashboardLoginResponse, EmailConfirmationStatus, MFAStatus } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

interface PasskeyAuthButtonProps {
  onSuccess: (response: DashboardLoginResponse) => void;
}

export const PasskeyAuthButton: React.FC<PasskeyAuthButtonProps> = ({ onSuccess }) => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const executeReCAPTCHA = useReCAPTCHA();
  const isSupported = isWebAuthnSupported();
  const [authenticatedResponse, setAuthenticatedResponse] = useState<DashboardLoginResponse>();

  const loginMutation = useMutation({
    mutationFn: async () => {
      const { data: login } = await webauthnBeginLogin({ throwOnError: true });
      const credential = await getPublicKeyCredential(login.options);
      const reCaptchaToken = await executeReCAPTCHA(RecaptchaAction.Login);
      const { data: response } = await webauthnFinishLogin({
        body: {
          credential,
        },
        headers: { 'g-recaptcha-response': reCaptchaToken },
        throwOnError: true,
      });

      return {
        challengeName: ChallengeName[response.challenge_name],
        dashboardUserId: response.dashboard_user_id,
        defaultPlatformId: response.default_platform_id,
        emailConfirmationStatus: EmailConfirmationStatus.NOT_REQUIRED,
        mfaStatus: MFAStatus[response.mfa_status],
        phoneNumber: response.phone_number,
        sessionId: response.session_id,
      };
    },
    onSuccess: (response) => {
      setAuthenticatedResponse(response);
      onSuccess(response);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }

      const content = error instanceof Error ? error.message : 'Failed to sign in with a passkey. Please try again.';
      addDangerNotification({ content, display: 'page-login' });
    },
  });

  return (
    <Button
      type="button"
      fullWidth
      variant="secondary"
      ariaLabel={authenticatedResponse ? 'Passkey verified' : undefined}
      isDisabled={!isSupported || loginMutation.isPending || !!authenticatedResponse}
      isLoading={loginMutation.isPending || !!authenticatedResponse}
      content={
        authenticatedResponse ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <CompletionCheck className="size-4" />
          </div>
        ) : undefined
      }
      onClick={() => {
        if (!authenticatedResponse) {
          loginMutation.mutate();
        }
      }}
    >
      Sign in with a passkey
    </Button>
  );
};
